import { useCallback, useEffect, useMemo, useState } from 'react';

import { api, type EllenorzesNap, type PenzTetel, type SoforAllapot } from '../api.ts';
import { useAlapadatok } from '../alapadatok-context.ts';
import { ft, szam } from '../formazas.ts';
import { elojeles, elszamolasMentese } from './elszamolas-dokumentum.ts';
import { EtteremKereso } from './EtteremKereso.tsx';
import { FpluszJelolo } from './FpluszJelolo.tsx';
import { Mezo, SzamMezo, ValasztoMezo } from './Mezo.tsx';
import { TenyMezo } from './TenyMezo.tsx';

/**
 * Egy sofőr elszámolása.
 *
 * A teljes járatlap látszik, nem csak a sofőr sorai — mert a kitöltés maga a
 * kiosztás. Ami már máshoz tartozik, az szürkén, zárolva jelenik meg a
 * tulajdonos nevével.
 */
export function EllenorzesSofor({
  datum,
  allapot,
  ellenorzes,
  onFrissit,
  onHiba,
}: {
  datum: string;
  allapot: SoforAllapot;
  ellenorzes: EllenorzesNap;
  onFrissit: (uj: EllenorzesNap) => void;
  onHiba: (uzenet: string) => void;
}) {
  const alapadatok = useAlapadatok();
  const [penzTetelek, setPenzTetelek] = useState<PenzTetel[]>([]);
  /** Nyitva van-e az „utólag bejött cím" kereső. */
  const [ujCim, setUjCim] = useState(false);
  const lezarva = allapot.lezarva;

  const penzBetolt = useCallback(async () => {
    try {
      const v = await api.get<{ tetelek: PenzTetel[] }>(
        `/api/ellenorzes/${datum}/penz/${allapot.sofor_id}`,
      );
      setPenzTetelek(v.tetelek);
    } catch {
      setPenzTetelek([]);
    }
  }, [datum, allapot.sofor_id]);

  useEffect(() => {
    void penzBetolt();
  }, [penzBetolt]);

  /** Közös hívásburkoló: frissíti az egész napot, mert minden mindent érint. */
  const hivas = useCallback(
    async (mit: () => Promise<EllenorzesNap>) => {
      try {
        onFrissit(await mit());
      } catch (h) {
        onHiba(h instanceof Error ? h.message : 'A művelet nem sikerült.');
        throw h;
      }
    },
    [onFrissit, onHiba],
  );

  const tenyMent = (tetel_id: number, mezo: string) => async (ertek: string) => {
    await hivas(() =>
      api.patch<EllenorzesNap>(`/api/ellenorzes/tetel/${tetel_id}/mezo/${mezo}`, {
        ertek,
        sofor_id: allapot.sofor_id,
      }),
    );
  };

  const elszamolasMent = (mezo: string) => async (ertek: string) => {
    await hivas(() =>
      api.patch<EllenorzesNap>(
        `/api/ellenorzes/${datum}/sofor/${allapot.sofor_id}/mezo/${mezo}`,
        { ertek },
      ),
    );
  };

  const fizetesiLehetosegek = useMemo(
    () =>
      alapadatok.fizetesi_modok
        .filter((m) => m.kod !== 'KP')
        .map((m) => ({ ertek: m.kod, cimke: m.nev })),
    [alapadatok.fizetesi_modok],
  );

  const o = allapot.osszesites;

  /*
   * A sofőr fülén **az ő címei** állnak, abban a sorrendben, ahogy a
   * nyomtatott lapon voltak — vagyis ahogy végigjárta.
   * (A megrendelő kérése, 2026-10-22.)
   *
   * A többi cím nincs eltüntetve: a kapcsolóval előjönnek, és onnan át
   * lehet venni bármelyiket, ahogy eddig. Ha ehhez a sofőrhöz egyetlen
   * cím sincs kiosztva, magától az egész lap látszik — így a kiosztás
   * nélkül tervezett nap ugyanúgy működik, mint eddig.
   */
  const sajatSorok = ellenorzes.sorok.filter((s) => s.sofor_id === allapot.sofor_id);
  const [mindMutat, setMindMutat] = useState(false);
  const mutassMindet = mindMutat || sajatSorok.length === 0;

  const sorok = useMemo(() => {
    const lista = mutassMindet ? ellenorzes.sorok : sajatSorok;
    if (!lista.some((s) => s.tura_sorrend != null)) return lista;
    return [...lista].sort(
      (a, b) => (a.tura_sorrend ?? 9999) - (b.tura_sorrend ?? 9999) || a.sorrend - b.sorrend,
    );
  }, [ellenorzes.sorok, mutassMindet, sajatSorok]);

  return (
    <>
      {/*
        Mi látszik és mi nem. (2026-10-22.)
        A sáv mindig megmondja, hány cím tartozik a sofőrhöz, és ha maradt
        üresen kitöltetlen sora, arról külön szól — de nem tiltja a lezárást.
      */}
      <div className="turasav">
        <span className="c">
          {sajatSorok.length > 0
            ? `${allapot.nev}: ${sajatSorok.length} cím`
            : 'Ehhez a sofőrhöz még nincs cím kiosztva — a teljes lap látszik.'}
          {sajatSorok.some((s) => s.tura_sorrend != null) && ' · túrasorrendben'}
        </span>
        <span style={{ flex: 1 }} />
        {sajatSorok.length > 0 && (
          <label className="c" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="checkbox"
              checked={mindMutat}
              onChange={(e) => setMindMutat(e.target.checked)}
            />
            A többi cím is
          </label>
        )}
      </div>

      {allapot.uresen > 0 && (
        <div className="uzenet flag" style={{ marginBottom: 8 }}>
          <b>
            {allapot.uresen} címébe nem írtunk semmit
            {allapot.uresen_nevek.length > 0 ? ':' : '.'}
          </b>{' '}
          {allapot.uresen_nevek.slice(0, 6).join(', ')}
          {allapot.uresen_nevek.length > 6 ? ` és még ${allapot.uresen_nevek.length - 6}` : ''}
          {'. '}
          A sofőr így is lezárható — lehet, hogy csak technikai cím volt, vagy nem járt ott.
        </div>
      )}

      <div className="sheet">
        <table>
          <thead>
            <tr>
              <th className="n">#</th>
              <th className="n kan">
                K.
                <br />
                vis
              </th>
              <th className="n kan">
                K.
                <br />
                ki
              </th>
              <th>Étterem</th>
              <th className="n g">MO</th>
              <th className="n">FO</th>
              <th className="n" title="Kihelyezett hordó">
                HO
              </th>
              <th className="n g">Mennyiség</th>
              <th className="n">Ár</th>
              <th className="n" style={{ width: 128 }}>
                Összeg
              </th>
              <th className="g">Fizetés</th>
              <th>Cím</th>
              <th
                className="g"
                style={{ minWidth: 118 }}
                title="A sor sofőrje — a legördülővel a cím átadható másik sofőrnek, a beírt mennyiségekkel együtt"
              >
                Sofőr
              </th>
            </tr>
          </thead>
          <tbody>
            {sorok.map((s, i) => {
              const enyem = s.sofor_id === allapot.sofor_id;
              const masse = s.sofor_id !== null && !enyem;
              const zarolt = lezarva || masse;
              // A sor jelenlegi sofőrjének lezárt elszámolásából nem lehet elvinni.
              const tulajLezarva =
                s.sofor_id !== null && !!ellenorzes.soforok.find((x) => x.sofor_id === s.sofor_id)?.lezarva;
              // A kiadott kanna a literből számolt, amíg kézzel felül nem írták.
              const kannaSzamitott = !s.kanna_ki_kezi && s.kanna_ki !== null;

              return (
                <tr
                  key={s.id}
                  className={`${s.utolag ? 'late ' : ''}${masse ? 'other' : enyem ? 'mine' : 'szabad'}`}
                >
                  <td className="idx">{i + 1}</td>
                  <td className="q">
                    <TenyMezo
                      terv={null}
                      ertek={s.kanna_vissza}
                      onMent={tenyMent(s.id, 'kanna_vissza')}
                      tiltva={zarolt}
                      oszlop="kanna_vissza"
                      sorIndex={i}
                      cim="Visszavett kanna — ezt kell kézzel beírni."
                    />
                  </td>
                  <td className="q">
                    <TenyMezo
                      terv={null}
                      ertek={s.kanna_ki}
                      onMent={tenyMent(s.id, 'kanna_ki')}
                      tiltva={zarolt}
                      oszlop="kanna_ki"
                      sorIndex={i}
                      osztaly={kannaSzamitott ? 'kkauto' : undefined}
                      cim={
                        kannaSzamitott
                          ? 'A literből számolva — felülírható. Ha kiüríted, visszaáll a számítottra.'
                          : s.kanna_ki_kezi
                            ? 'Kézzel beírva — a liter változása nem írja át. Kiürítve visszaáll a számítottra.'
                            : 'Kiadott kanna — a liter beírásakor magától kitöltődik.'
                      }
                    />
                  </td>
                  <td className="nm">
                    <b>
                      {s.etterem_nev}
                      {(s.terv_hordo ?? 0) > 0 && (
                        <span className="hojel">+{szam(s.terv_hordo ?? 0, 0)} HO</span>
                      )}
                      <FpluszJelolo
                        ertek={s.fplusz}
                        vanAra={s.van_fplusz}
                        tiltva={zarolt}
                        onMent={tenyMent(s.id, 'fplusz')}
                      />
                      {s.mo_egyedi && (
                        <span className="mojel" title="Egyedi moslékár — eltér az alapártól">
                          MO {ft(s.mo_egyedi)}
                        </span>
                      )}
                    </b>
                  </td>
                  <td className={`q g${s.terv_mo ? ' pl' : ''}`}>
                    <TenyMezo
                      terv={s.terv_mo}
                      ertek={s.teny_mo}
                      onMent={tenyMent(s.id, 'teny_mo')}
                      tiltva={zarolt}
                      oszlop="teny_mo"
                      sorIndex={i}
                    />
                  </td>
                  <td className={`q${s.terv_fo ? ' pl' : ''}`}>
                    <TenyMezo
                      terv={s.terv_fo}
                      ertek={s.teny_fo}
                      onMent={tenyMent(s.id, 'teny_fo')}
                      tiltva={zarolt}
                      oszlop="teny_fo"
                      sorIndex={i}
                    />
                  </td>
                  <td className={`q${s.terv_hordo ? ' pl' : ''}`}>
                    <TenyMezo
                      terv={s.terv_hordo}
                      ertek={s.teny_hordo}
                      onMent={tenyMent(s.id, 'teny_hordo')}
                      tiltva={zarolt}
                      oszlop="teny_hordo"
                      sorIndex={i}
                    />
                  </td>
                  <td className={`q g${s.terv_liter || s.terv_liter_x ? ' pl' : ''}`}>
                    <TenyMezo
                      terv={s.terv_liter}
                      tervX={s.terv_liter_x}
                      ertek={s.teny_liter}
                      onMent={tenyMent(s.id, 'teny_liter')}
                      tiltva={zarolt}
                      oszlop="teny_liter"
                      sorIndex={i}
                      osztaly={s.fplusz ? 'fpq' : undefined}
                      cim={s.fplusz ? 'F+ olaj — a partner F+ árával számol.' : undefined}
                    />
                  </td>
                  <td className="q">
                    <TenyMezo
                      terv={null}
                      ertek={s.egysegar}
                      onMent={tenyMent(s.id, 'egysegar')}
                      tiltva={zarolt}
                      oszlop="egysegar"
                      sorIndex={i}
                      helykitolto="—"
                    />
                  </td>
                  <td className="sum">{s.osszeg ? ft(s.osszeg) : ''}</td>
                  <td className="g">
                    <ValasztoMezo
                      ertek={s.fizetesi_mod}
                      uresCimke="Készpénz"
                      lehetosegek={fizetesiLehetosegek}
                      tiltva={zarolt}
                      osztaly={
                        s.fizetesi_mod === 'Á' ? 'p tr' : s.fizetesi_mod === 'R' ? 'p rr' : 'p'
                      }
                      onMent={tenyMent(s.id, 'fizetesi_mod')}
                    />
                  </td>
                  <td className="addr">
                    {[s.etterem_irsz, s.etterem_cim].filter(Boolean).join(' ') || '—'}
                  </td>
                  <td className="act edrv">
                    <div className="edrv-dob">
                    <select
                      className={`p${enyem ? ' drvon' : ''}`}
                      value={s.sofor_id ?? ''}
                      disabled={tulajLezarva}
                      title={
                        tulajLezarva
                          ? 'A sofőr elszámolása le van zárva — innen nem vihető el'
                          : 'Átadás másik sofőrnek — a beírt mennyiségek a sorral mennek'
                      }
                      onChange={(e) => {
                        const uj = e.target.value;
                        void hivas(() =>
                          api.post<EllenorzesNap>(`/api/ellenorzes/tetel/${s.id}/atadas`, {
                            sofor_id: uj === '' ? null : Number(uj),
                          }),
                        ).catch(() => undefined);
                      }}
                    >
                      <option value="">—</option>
                      {ellenorzes.soforok.map((so) => (
                        <option
                          key={so.sofor_id}
                          value={so.sofor_id}
                          disabled={so.lezarva && so.sofor_id !== s.sofor_id}
                        >
                          {so.nev}
                        </option>
                      ))}
                    </select>
                    {enyem && !lezarva && (
                      <button
                        className="xb"
                        title="A sor felszabadítása — a beírt mennyiségek törlődnek, és a sor bármelyik sofőrnél újra felvehető."
                        onClick={() => {
                          if (
                            !confirm(
                              `Felszabadítod ezt a sort? A beírt mennyiségek törlődnek.\n\n${s.etterem_nev}`,
                            )
                          )
                            return;
                          void hivas(() =>
                            api.post<EllenorzesNap>(
                              `/api/ellenorzes/tetel/${s.id}/felszabadit`,
                            ),
                          );
                        }}
                      >
                        ↩
                      </button>
                    )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {sorok.length > 0 && (
            <tfoot>
              <tr>
                <td></td>
                <td>{szam(o.kanna_vissza, 0)}</td>
                <td>{szam(o.kanna_ki, 0)}</td>
                <td className="lab">Összesen</td>
                <td className="g">{szam(o.mo_db, 0)}</td>
                <td>{szam(o.fo_db, 0)}</td>
                <td>{szam(o.hordo_db, 0)}</td>
                <td className="g">{szam(o.liter, 0)}</td>
                <td></td>
                <td>{ft(o.eladas)}</td>
                <td className="g"></td>
                <td></td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {sorok.length === 0 && <div className="blank">Ez a nap üres.</div>}

      {/* Utólag bejött cím: a sor a nap végére kerül, minden sofőrnél látszik,
          és azé lesz, aki a tényleges mennyiséget beírja. */}
      <div className="actbar" style={{ marginTop: 8 }}>
        <button
          className={`btn sm${ujCim ? ' pri' : ''}`}
          disabled={lezarva}
          title={lezarva ? 'Ennek a sofőrnek a napja le van zárva' : 'Utólag bejött cím felvétele a naphoz'}
          onClick={() => setUjCim((v) => !v)}
        >
          {ujCim ? '× Mégsem' : '+ cím (utólag bejött)'}
        </button>
        {ujCim && (
          <span className="hint">
            A cím a nap végére kerül „utólag" jelöléssel, és minden sofőrnél látszik — azé lesz, aki
            a tényleges mennyiséget beírja.
          </span>
        )}
      </div>
      {ujCim && (
        <div style={{ maxWidth: 420, marginBottom: 12 }}>
          <EtteremKereso
            fejlec="Utólag bejött cím"
            fizetesiLehetosegek={alapadatok.fizetesi_modok.map((m) => ({ ertek: m.kod, cimke: m.nev }))}
            gombCimke="Hozzáadás"
            gombStilus="pri"
            bovitett
            onValaszt={async (e, ertekek) => {
              await hivas(() =>
                api.post<EllenorzesNap>(`/api/ellenorzes/${datum}/tetel`, {
                  etterem_id: e.id,
                  terv_mo: ertekek.mo,
                  terv_fo: ertekek.fo,
                  terv_hordo: ertekek.hordo,
                  terv_liter: ertekek.mennyiseg,
                  fplusz: ertekek.fplusz,
                  fizetesi_mod: ertekek.fizetesi_mod || null,
                }),
              );
              setUjCim(false);
            }}
          />
        </div>
      )}

      <div className="cols">
        {/* --- a kézzel felvett beszedés és kiadás --- */}
        <div className="panel">
          <PenzLista
            cim="Beszedés"
            tipus="beszedes"
            hsoKiloAr={o.hso_kilo_ar}
            tetelek={penzTetelek.filter((p) => p.tipus === 'beszedes')}
            lezarva={lezarva}
            datum={datum}
            sofor_id={allapot.sofor_id}
            onValtozott={async () => {
              await penzBetolt();
            }}
            hivas={hivas}
          />
          <PenzLista
            cim="Kiadás"
            tipus="kiadas"
            hsoKiloAr={o.hso_kilo_ar}
            tetelek={penzTetelek.filter((p) => p.tipus === 'kiadas')}
            lezarva={lezarva}
            datum={datum}
            sofor_id={allapot.sofor_id}
            onValtozott={async () => {
              await penzBetolt();
            }}
            hivas={hivas}
          />
          <p className="hint" style={{ marginTop: 10 }}>
            A moslék, a kanna és a hordó a címeknél beírt darabszámokból magától számolódik
            — ide csak az kerüljön, ami azon felül történt.
          </p>
        </div>

        {/* --- összesítő (javitasok-2.0-teljes.md 4.) --- */}
        <div className="panel">
          <p className="lbl">Összesítő — {allapot.nev}</p>
          <div className="kv">
            <span>Liter</span>
            <b>{szam(o.liter, 1)}</b>
          </div>
          <div className="kv">
            <span>Eladás (bruttó)</span>
            <b>{ft(o.eladas)}</b>
          </div>
          <div className="kv sub">
            <span>ebből áfa {alapadatok.afa_kulcs}%</span>
            <b>{ft(o.afa)}</b>
          </div>
          <div className="kv sub">
            <span>átlagár</span>
            <b>{o.atlagar === null ? '—' : `${szam(o.atlagar, 2)} Ft/l`}</b>
          </div>
          <div className="kv sub">
            <span>ebből nem készpénzes</span>
            <b>{ft(o.nem_keszpenzes)}</b>
          </div>

          <p className="lbl" style={{ marginTop: 16 }}>
            Moslék és göngyöleg
          </p>
          <div className="kv">
            <span>Moslék (MO)</span>
            <b>{ft(o.mo_osszeg)} Ft</b>
          </div>
          <div className="kv sub">
            <span>darab · ebből készpénzes</span>
            <b>
              {szam(o.mo_db, 0)} db · {ft(o.mo_keszpenzes)} Ft
            </b>
          </div>
          <div
            className="kv"
            title="Kiadott kanna után az ügyfél fizet, a visszavett után visszajár neki. Csak a készpénzes sorok számítanak — a többi a számlán jelenik meg."
          >
            <span>Kanna betétdíj (készpénzes)</span>
            <b>{elojeles(o.kanna_keszpenzes)} Ft</b>
          </div>
          <div className="kv sub">
            <span>kiadott / visszavett</span>
            <b>
              {szam(o.kanna_ki, 0)} db / {szam(o.kanna_vissza, 0)} db
            </b>
          </div>
          <div className="kv">
            <span>Hordó betétdíj (készpénzes)</span>
            <b>{ft(o.hordo_keszpenzes)} Ft</b>
          </div>
          <div className="kv sub">
            <span>kihelyezett hordó</span>
            <b>{szam(o.hordo_db, 0)} db</b>
          </div>
          <div className="kv">
            <span>HSO kifizetés</span>
            <b>{ft(o.hso_osszeg)} Ft</b>
          </div>
          <div className="kv sub">
            <span>ebből súly</span>
            <b>
              {szam(o.hso_kg, 0)} kg · {ft(o.hso_kilo_ar)} Ft/kg
            </b>
          </div>
          <div className="kv">
            <span>Raktári (R)</span>
            <b>{szam(o.raktari_liter, 1)} l</b>
          </div>

          {/*
            * Mit mondanak az aznapi szállítólevelei. Nem akadálya a
            * lezárásnak — az eltérésnek sokszor jó oka van —, de az iroda
            * lássa, ha ránézésre nem stimmel. (2026-10-05)
            */}
          <p className="lbl" style={{ marginTop: 16 }}>
            Szállítólevelek ezen a napon — {szam(allapot.okmanyok.okmany_db, 0)} okmány
          </p>
          {allapot.okmanyok.okmany_db === 0 ? (
            <div className="kv sub">
              <span>nincs a nevére kiállított okmány</span>
              <b>—</b>
            </div>
          ) : (
            <>
              {/*
                * Mindkét fajtánál a **kiállított hordó** darabszáma áll elöl, a
                * HSO-nál mellette a tömeg. Korábban az okmányszám és a
                * hordószám összekeverhető volt — három moslékos okmány nem
                * három hordó. Ezért az okmányszám a fejlécbe került, és alább
                * okmányonként is látszik, melyiken mennyi áll.
                * (2026-10-09, a megrendelő jelzése)
                */}
              <div
                className={`kv${allapot.okmanyok.fo_elter ? ' flag' : ''}`}
                title="A fáradt olajos (HSO) szállítóleveleken kiállított hordó és tömeg. Az elszámolásba a kg megy."
              >
                <span>Fáradt olaj (HSO)</span>
                <b>
                  {szam(allapot.okmanyok.fo_hordo, 0)} hordó ·{' '}
                  {szam(allapot.okmanyok.fo_kg, 0)} kg
                </b>
              </div>
              <div className="kv sub">
                <span>a járatlapon</span>
                <b>{szam(o.fo_db, 0)} hordó</b>
              </div>
              <div
                className={`kv${allapot.okmanyok.mo_elter ? ' flag' : ''}`}
                title="A moslékos szállítóleveleken kiállított hordó darabszáma. Az elszámolásba ez a szám megy."
              >
                <span>Moslék (MO)</span>
                <b>{szam(allapot.okmanyok.mo_hordo, 0)} hordó</b>
              </div>
              <div className="kv sub">
                <span>a járatlapon</span>
                <b>{szam(o.mo_db, 0)} hordó</b>
              </div>

              {/* Okmányonként, hogy ne kelljen a számokat fejben szétszedni. */}
              <table className="tt2 okmtab">
                <thead>
                  <tr>
                    <th>Sorszám</th>
                    <th>Fajta</th>
                    <th className="n">Hordó</th>
                    <th className="n">Tömeg</th>
                  </tr>
                </thead>
                <tbody>
                  {allapot.okmanyok.tetelek.map((t) => (
                    <tr key={t.szam}>
                      <td className="calc2">{t.szam}</td>
                      <td>{t.tipus}</td>
                      <td className="calc2">{t.hordo ? szam(t.hordo, 0) : '·'}</td>
                      <td className="calc2">{t.kg ? `${szam(t.kg, 0)} kg` : '·'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="kv sub">
                <span>a sztornózott okmányok nincsenek benne</span>
                <b>—</b>
              </div>
              {(allapot.okmanyok.fo_elter || allapot.okmanyok.mo_elter) && (
                <div className="uzenet flag" style={{ marginTop: 6 }}>
                  Az okmányok és a járatlap hordószáma eltér. Nem akadálya a lezárásnak — lehet,
                  hogy az okmány másik napra szól —, de érdemes ránézni.
                </div>
              )}
            </>
          )}

          <p className="lbl" style={{ marginTop: 16 }}>
            Leadó levezetése
          </p>
          <div className="kv sub">
            <span>készpénzes olajeladás</span>
            <b>{ft(o.keszpenzes)}</b>
          </div>
          <div className="kv sub">
            <span>+ moslék készpénzben</span>
            <b>{ft(o.mo_keszpenzes)}</b>
          </div>
          <div className="kv sub">
            <span>+ kanna készpénzben</span>
            <b>{elojeles(o.kanna_keszpenzes)}</b>
          </div>
          <div className="kv sub">
            <span>+ hordó készpénzben</span>
            <b>{ft(o.hordo_keszpenzes)}</b>
          </div>
          <div className="kv sub">
            <span>+ egyéb beszedés készpénzben</span>
            <b>{ft(o.beszedes_keszpenzes)}</b>
          </div>
          <div
            className="kv sub"
            title="Csak a készpénzes kiadás jön le: az átutalt kiadás a sofőr kasszáját nem érinti."
          >
            <span>− kiadás készpénzben</span>
            <b>
              {ft(o.kiadas_keszpenzes)}
              {o.kiadas !== o.kiadas_keszpenzes && (
                <span className="halvany" style={{ fontWeight: 400 }}>
                  {' '}
                  (össz. {ft(o.kiadas)})
                </span>
              )}
            </b>
          </div>
          <div className="kv tot">
            <span>Leadó</span>
            <b>{ft(o.leado)}</b>
          </div>
        </div>

        {/* --- készlet és pénz --- */}
        <div className="panel">
          <p className="lbl">Autókészlet — olaj</p>
          <div className="stk">
            <label>Nyitó</label>
            <div className="v calc">{szam(allapot.keszlet.nyito, 1)}</div>

            <label>Felrakodás</label>
            <div className="v">
              <SzamMezo
                ertek={allapot.felrakodas}
                onMent={elszamolasMent('felrakodas')}
                tiltva={lezarva}
                helykitolto="0"
              />
            </div>

            <label>Átadás másik autónak</label>
            <div className="v">
              <SzamMezo
                ertek={allapot.atadas}
                onMent={elszamolasMent('atadas')}
                tiltva={lezarva}
                helykitolto="0"
              />
            </div>

            <label>Kiszállítva</label>
            <div className="v calc">{szam(allapot.keszlet.kiszallitva, 1)}</div>
            <hr />

            <label>Számított záró</label>
            <div className="v calc">{szam(allapot.keszlet.szamitott_zaro, 1)}</div>

            <label>Megszámolt záró</label>
            <div className="v">
              <SzamMezo
                ertek={allapot.megszamolt_zaro}
                onMent={elszamolasMent('megszamolt_zaro')}
                tiltva={lezarva}
                helykitolto="—"
              />
            </div>
          </div>

          <p className="lbl" style={{ marginTop: 18 }}>
            Leadott készpénz
          </p>
          <div className="stk">
            <label>Számított leadó</label>
            <div className="v calc">{ft(allapot.penz.szamitott_leado)}</div>
            <label>Ténylegesen leadott</label>
            <div className="v">
              <SzamMezo
                ertek={allapot.leadott_keszpenz}
                onMent={elszamolasMent('leadott_keszpenz')}
                tiltva={lezarva}
                helykitolto="—"
              />
            </div>
          </div>
        </div>
      </div>

      {/* --- a két ellenőrzés és a lezárás --- */}
      <div className="chips">
        <Ellenorzo
          cimke="Készlet"
          rendben={allapot.keszlet.rendben}
          megvan={allapot.keszlet.megszamolt_zaro !== null}
          elteres={
            allapot.keszlet.elteres === null
              ? '—'
              : allapot.keszlet.rendben
                ? 'egyezik'
                : `${allapot.keszlet.elteres > 0 ? '+' : ''}${szam(allapot.keszlet.elteres, 1)} l`
          }
        />
        <Ellenorzo
          cimke="Pénz"
          rendben={allapot.penz.rendben}
          megvan={allapot.penz.leadott_keszpenz !== null}
          elteres={
            allapot.penz.elteres === null
              ? '—'
              : allapot.penz.rendben
                ? 'egyezik'
                : `${allapot.penz.elteres > 0 ? '+' : ''}${ft(allapot.penz.elteres)} Ft`
          }
        />

        <button
          className="btn sm"
          style={{ marginLeft: 'auto' }}
          disabled={allapot.sorok === 0 && !allapot.megkezdte}
          title="Nyomtatható elszámolási lap külön ablakban — onnan PDF-be is menthető."
          onClick={() =>
            void elszamolasMentese({
              datum,
              ellenorzes,
              soforok: [allapot],
              alapadatok,
            }).catch((h: unknown) =>
              onHiba(h instanceof Error ? h.message : 'Az elszámolási lap nem készült el.'),
            )
          }
        >
          Elszámolás mentése
        </button>

        {lezarva ? (
          <button
            className="btn"
            onClick={() =>
              void hivas(() =>
                api.post<EllenorzesNap>(
                  `/api/ellenorzes/${datum}/sofor/${allapot.sofor_id}/visszanyit`,
                ),
              )
            }
          >
            Visszanyitás
          </button>
        ) : (
          <button
            className="btn pri"
            disabled={!allapot.lezarhato}
            title={
              allapot.lezarhato
                ? allapot.figyelmeztetesek.join('\n') || undefined
                : allapot.akadalyok.join('\n')
            }
            onClick={() => {
              /*
                Az eltérés nem akadály: a sofőr így is lezárható, csak
                rákérdezünk. (A megrendelő kérése, 2026-09-18)
              */
              if (
                allapot.figyelmeztetesek.length > 0 &&
                !window.confirm(
                  `${allapot.nev} elszámolásában eltérés van:\n\n· ${allapot.figyelmeztetesek.join(
                    '\n· ',
                  )}\n\nÍgy is lezárod? Az eltérés a naplóban és az autókészletnél megmarad.`,
                )
              ) {
                return;
              }
              void hivas(() =>
                api.post<EllenorzesNap>(
                  `/api/ellenorzes/${datum}/sofor/${allapot.sofor_id}/lezar`,
                  { megerosit: true },
                ),
              );
            }}
          >
            Sofőr lezárása
          </button>
        )}
      </div>

      {!lezarva && (allapot.akadalyok.length > 0 || allapot.figyelmeztetesek.length > 0) && (
        <div className="warns">
          {[...allapot.akadalyok, ...allapot.figyelmeztetesek].map((a) => (
            <div className="warn" key={a}>
              {a}
            </div>
          ))}
        </div>
      )}

      {lezarva && (
        <div className="uzenet ok">
          {allapot.nev} elszámolása lezárva. A sorai zároltak — módosításhoz nyisd vissza.
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */

function Ellenorzo({
  cimke,
  rendben,
  megvan,
  elteres,
}: {
  cimke: string;
  rendben: boolean;
  megvan: boolean;
  elteres: string;
}) {
  const allapot = !megvan ? 'ures' : rendben ? 'ok' : 'bad';
  return (
    <div className={`chip ${allapot}`}>
      <span className="mk">{allapot === 'ok' ? '✓' : allapot === 'bad' ? '!' : '•'}</span>
      <span>
        <em>{cimke}</em>
        <b>{elteres}</b>
      </span>
    </div>
  );
}

function PenzLista({
  cim,
  tipus,
  tetelek,
  lezarva,
  datum,
  sofor_id,
  hsoKiloAr,
  onValtozott,
  hivas,
}: {
  cim: string;
  tipus: 'beszedes' | 'kiadas';
  tetelek: PenzTetel[];
  lezarva: boolean;
  datum: string;
  sofor_id: number;
  /** A HSO kilóára a termékek törzsből — a fejlécben látszik. (2.0) */
  hsoKiloAr: number;
  onValtozott: () => Promise<void>;
  hivas: (mit: () => Promise<EllenorzesNap>) => Promise<void>;
}) {
  const alapadatok = useAlapadatok();
  const kategoriak = alapadatok.kategoriak.filter((k) => k.tipus === tipus);

  /**
   * Súly szerint számol-e ez a kategória? (2.0)
   * A HSO-nál nem összeget írunk, hanem kilogrammot.
   */
  const kategoriaNev = (kategoria_id: number | null) =>
    kategoria_id === null
      ? ''
      : (alapadatok.kategoriak.find((k) => k.id === kategoria_id)?.nev.trim().toUpperCase() ?? '');
  const sulySzerinti = (kategoria_id: number | null) => kategoriaNev(kategoria_id) === 'HSO';
  /** A MO beszedésnél hordó darabszámot írunk, az összeg a MO listaárból jön. (2026-09-17) */
  const darabSzerinti = (kategoria_id: number | null) =>
    tipus === 'beszedes' && kategoriaNev(kategoria_id) === 'MO';
  /** A tankolás mellé szabadon beírható a rendszám. (2026-09-17) */
  const tankolas = (kategoria_id: number | null) =>
    tipus === 'kiadas' && kategoriaNev(kategoria_id) === 'TANKOLÁS';
  /**
   * Az „Egyéb" mellé szabadon beírható, hogy mi volt — beszedésnél és
   * kiadásnál is. A szöveg az összesítőn külön soron látszik.
   * (A megrendelő kérése, 2026-09-18)
   */
  const egyeb = (kategoria_id: number | null) => kategoriaNev(kategoria_id) === 'EGYÉB';

  return (
    <>
      <p className="lbl" style={{ marginTop: cim === 'Kiadás' ? 16 : 0 }}>
        {cim}
        {cim === 'Kiadás' && hsoKiloAr > 0 && (
          <span className="hint" style={{ marginLeft: 10, textTransform: 'none', letterSpacing: 0 }}>
            HSO: {ft(hsoKiloAr)} Ft/kg
          </span>
        )}
      </p>

      {tetelek.map((t) => (
        <div
          className={`penzsor${
            sulySzerinti(t.kategoria_id) ||
            darabSzerinti(t.kategoria_id) ||
            tankolas(t.kategoria_id) ||
            egyeb(t.kategoria_id)
              ? ' ot'
              : ''
          }`}
          key={t.id}
        >
          <ValasztoMezo
            ertek={t.kategoria_id === null ? '' : String(t.kategoria_id)}
            uresCimke="— kategória —"
            lehetosegek={kategoriak.map((k) => ({ ertek: String(k.id), cimke: k.nev }))}
            tiltva={lezarva}
            onMent={async (uj) => {
              await hivas(() =>
                api.patch<EllenorzesNap>(`/api/ellenorzes/penz/${t.id}/mezo/kategoria_id`, {
                  ertek: uj,
                }),
              );
              await onValtozott();
            }}
          />
          {sulySzerinti(t.kategoria_id) ? (
            <>
              {/*
                A használt sütőolajat kilóra vesszük át: a súlyt írjuk be, az
                összeget a program szorozza ki a termékek törzsben álló
                kilóárral. Így nem lehet elszámolni.
              */}
              <SzamMezo
                ertek={t.mennyiseg_kg}
                helykitolto="kg"
                osztaly="c"
                cim="Súly kilogrammban — az összeg ebből számolódik."
                tiltva={lezarva}
                onMent={async (uj) => {
                  await hivas(() =>
                    api.patch<EllenorzesNap>(
                      `/api/ellenorzes/penz/${t.id}/mezo/mennyiseg_kg`,
                      { ertek: uj },
                    ),
                  );
                  await onValtozott();
                }}
              />
              <span className="calc2 hso-osszeg" title="A kilóból számolt összeg.">
                {ft(t.osszeg)}
              </span>
            </>
          ) : darabSzerinti(t.kategoria_id) ? (
            <>
              {/* MO: a hordók számát írjuk, az összeg a MO listaárból számolódik. */}
              <SzamMezo
                ertek={t.mennyiseg_db}
                helykitolto="hordó"
                osztaly="c"
                cim="Hordó darabszám — az összeg a Termékek törzs MO listaárából számolódik."
                tiltva={lezarva}
                onMent={async (uj) => {
                  await hivas(() =>
                    api.patch<EllenorzesNap>(`/api/ellenorzes/penz/${t.id}/mezo/mennyiseg_db`, {
                      ertek: uj,
                    }),
                  );
                  await onValtozott();
                }}
              />
              <span className="calc2 hso-osszeg" title="A hordók számából számolt összeg.">
                {ft(t.osszeg)}
              </span>
            </>
          ) : tankolas(t.kategoria_id) ? (
            <>
              <span className="rendszam">
                <Mezo
                  ertek={t.rendszam}
                  helykitolto="rendszám"
                  cim="A tankolt autó rendszáma — szabadon beírható."
                  tiltva={lezarva}
                  onMent={async (uj) => {
                    await hivas(() =>
                      api.patch<EllenorzesNap>(`/api/ellenorzes/penz/${t.id}/mezo/rendszam`, {
                        ertek: uj,
                      }),
                    );
                    await onValtozott();
                  }}
                />
              </span>
              <SzamMezo
                ertek={t.osszeg || null}
                helykitolto="0"
                osztaly="c"
                tiltva={lezarva}
                onMent={async (uj) => {
                  await hivas(() =>
                    api.patch<EllenorzesNap>(`/api/ellenorzes/penz/${t.id}/mezo/osszeg`, {
                      ertek: uj,
                    }),
                  );
                  await onValtozott();
                }}
              />
            </>
          ) : egyeb(t.kategoria_id) ? (
            <>
              {/* Egyéb: ide írható, hogy mi volt — az összesítőn ezzel a szöveggel áll. */}
              <span className="rendszam">
                <Mezo
                  ertek={t.megjegyzes}
                  helykitolto="mi volt?"
                  cim="Szabadon beírható, mi volt ez a tétel — az összesítőn külön soron látszik."
                  tiltva={lezarva}
                  onMent={async (uj) => {
                    await hivas(() =>
                      api.patch<EllenorzesNap>(`/api/ellenorzes/penz/${t.id}/mezo/megjegyzes`, {
                        ertek: uj,
                      }),
                    );
                    await onValtozott();
                  }}
                />
              </span>
              <SzamMezo
                ertek={t.osszeg || null}
                helykitolto="0"
                osztaly="c"
                tiltva={lezarva}
                onMent={async (uj) => {
                  await hivas(() =>
                    api.patch<EllenorzesNap>(`/api/ellenorzes/penz/${t.id}/mezo/osszeg`, {
                      ertek: uj,
                    }),
                  );
                  await onValtozott();
                }}
              />
            </>
          ) : (
            <SzamMezo
              ertek={t.osszeg || null}
              helykitolto="0"
              osztaly="c"
              tiltva={lezarva}
              onMent={async (uj) => {
                await hivas(() =>
                  api.patch<EllenorzesNap>(`/api/ellenorzes/penz/${t.id}/mezo/osszeg`, {
                    ertek: uj,
                  }),
                );
                await onValtozott();
              }}
            />
          )}
          <ValasztoMezo
            ertek={t.fizetesi_mod}
            uresCimke="Készpénz"
            lehetosegek={alapadatok.fizetesi_modok
              .filter((m) => m.kod !== 'KP')
              .map((m) => ({ ertek: m.kod, cimke: m.nev }))}
            tiltva={lezarva}
            onMent={async (uj) => {
              await hivas(() =>
                api.patch<EllenorzesNap>(`/api/ellenorzes/penz/${t.id}/mezo/fizetesi_mod`, {
                  ertek: uj,
                }),
              );
              await onValtozott();
            }}
          />
          <button
            className="xb"
            title="Tétel törlése"
            disabled={lezarva}
            onClick={async () => {
              await hivas(() => api.torol<EllenorzesNap>(`/api/ellenorzes/penz/${t.id}`));
              await onValtozott();
            }}
          >
            ×
          </button>
        </div>
      ))}
      <button
        className="btn sm"
        style={{ marginTop: 4 }}
        disabled={lezarva}
        onClick={async () => {
          await hivas(() =>
            api.post<EllenorzesNap>(`/api/ellenorzes/${datum}/penz/${sofor_id}`, {
              tipus,
              kategoria_id: kategoriak[0]?.id ?? null,
            }),
          );
          await onValtozott();
        }}
      >
        + tétel
      </button>
    </>
  );
}
